import { useEffect, useRef, useCallback, useState } from 'react';
import { isAdyenGooglePayEnabled } from 'utils/adyen';
import adyen from '../../../payments/core/engines/adyen';

/**
 * useAdyenGooglePaySdk hook.
 * Manage Adyen Google Pay SDK component lifecycle: create, mount, unmount, and update.
 * This hook is framework-agnostic (no Redux, i18n, or error monitoring) and receives
 * all configuration and callbacks as plain parameters.
 *
 * @param {object} props - adyenConfig, currency, countryCode, merchantName, amount, onClick, onSubmit, onError.
 * @param {object} props.adyenConfig - Adyen configuration object from whitelabel env (contains googlePay sub-config).
 * @param {string} props.currency - Currency code (e.g. "MXN").
 * @param {string} props.countryCode - Country code (e.g. "MX").
 * @param {string} props.merchantName - Fallback merchant name from whitelabel copies.
 * @param {number} props.amount - Numeric purchase total.
 * @param {Function} props.onClick - Callback for Google Pay button click (resolve, reject) => void.
 * @param {Function} props.onSubmit - Callback for payment submission (state, component) => void.
 * @param {Function} props.onError - Callback for component errors (adyenError) => void.
 * @returns {object} Google Pay SDK component state and container ref.
 */
function useAdyenGooglePaySdk({
  adyenConfig,
  currency,
  countryCode,
  merchantName,
  amount,
  onClick,
  onSubmit,
  onError,
}) {
  const googlePayComponentRef = useRef(null);
  const containerRef = useRef(null);
  const isInitialized = useRef(false);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setErrorState] = useState(null);
  const [isAvailable, setIsAvailable] = useState(false);

  const getValidAmount = useCallback((value) => {
    const numValue = Number(value);
    return Number.isFinite(numValue) && numValue > 0 ? numValue : 0;
  }, []);

  const amountRef = useRef(getValidAmount(amount));
  const onClickRef = useRef(onClick);
  const onSubmitRef = useRef(onSubmit);
  const onErrorRef = useRef(onError);

  onClickRef.current = onClick;
  onSubmitRef.current = onSubmit;
  onErrorRef.current = onError;

  const stableOnClick = useCallback((...args) => {
    onClickRef.current?.(...args);
  }, []);

  const stableOnSubmit = useCallback((...args) => {
    onSubmitRef.current?.(...args);
  }, []);

  const stableOnError = useCallback((...args) => {
    onErrorRef.current?.(...args);
  }, []);

  /**
   * Initialize Google Pay component.
   */
  useEffect(() => {
    let isMounted = true;

    if (!containerRef.current) return;
    if (isInitialized.current) return;

    if (!isAdyenGooglePayEnabled()) {
      setErrorState('google_pay_not_enabled');
      setIsLoading(false);
      return;
    }

    const adyenInstance = adyen.getAdyen();
    if (!adyenInstance) {
      console.error('Adyen instance not initialized. Cannot create Google Pay component.');
      setErrorState('adyen_not_initialized');
      setIsLoading(false);
      return;
    }

    if (!adyenConfig || !adyenConfig.googlePay) {
      console.error('Google Pay configuration missing. Cannot create Google Pay component.');
      setErrorState('google_pay_config_missing');
      setIsLoading(false);
      return;
    }

    const {
      merchantName: configMerchantName,
      merchantId,
      gatewayMerchantId,
      buttonType,
      buttonColor,
    } = adyenConfig.googlePay;

    try {
      setIsLoading(true);
      setErrorState(null);

      const componentConfig = {
        amount: {
          value: Math.round(getValidAmount(amountRef.current) * 100),
          currency,
        },
        countryCode: countryCode || 'MX',
        configuration: {
          merchantName: configMerchantName || merchantName,
          merchantId,
          gatewayMerchantId,
        },
        buttonType: buttonType || 'long',
        buttonColor: buttonColor || 'default',
        onClick: stableOnClick,
        onSubmit: stableOnSubmit,
        onError: stableOnError,
      };

      const googlePayComponent = adyen.createGooglePay(componentConfig);

      if (!googlePayComponent) {
        setErrorState('google_pay_not_available');
        setIsLoading(false);
        return;
      }

      // In Adyen Web SDK v4, isAvailable() always resolves with true/false.
      // A .catch() is included defensively for forward-compatibility with future SDK versions.
      googlePayComponent
        .isAvailable()
        .then((available) => {
          if (!isMounted) return;

          if (available) {
            if (!containerRef.current) return;

            googlePayComponent.mount(containerRef.current);
            googlePayComponentRef.current = googlePayComponent;
            isInitialized.current = true;
            setIsAvailable(true);
          } else {
            console.info('Google Pay not available on this device/browser');
            setErrorState('google_pay_not_available');
          }

          setIsLoading(false);
        })
        .catch((availabilityError) => {
          if (!isMounted) return;

          console.error('Google Pay availability check failed:', availabilityError);
          setErrorState('google_pay_not_available');
          setIsLoading(false);
        });
    } catch (err) {
      console.error('Error creating Google Pay component:', err);
      setErrorState('google_pay_initialization_error');
      setIsLoading(false);
    }

    return () => {
      isMounted = false;

      if (googlePayComponentRef.current) {
        try {
          googlePayComponentRef.current.unmount();
          googlePayComponentRef.current = null;
          isInitialized.current = false;
          setIsAvailable(false);
        } catch (err) {
          console.error('Error unmounting Google Pay component:', err);
        }
      }
    };
    // Stable callbacks read latest handlers from refs; only config changes remount the SDK.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adyenConfig, merchantName, currency, countryCode]);

  /**
   * Update Google Pay component amount when purchase total changes.
   */
  useEffect(() => {
    amountRef.current = getValidAmount(amount);

    if (!googlePayComponentRef.current) return;
    if (!isInitialized.current) return;

    try {
      googlePayComponentRef.current.update({
        amount: {
          value: Math.round(getValidAmount(amount) * 100),
          currency,
        },
      });
    } catch (err) {
      console.error('Error updating Google Pay amount:', err);
    }
  }, [amount, currency, getValidAmount]);

  return {
    containerRef,
    isLoading,
    error,
    isAvailable,
  };
}

export default useAdyenGooglePaySdk;
